import React, {useRef} from 'react'
import {Box} from '@chakra-ui/react'
import {usePage} from '@salesforce/commerce-sdk-react'
import {HeroBanner, DoubleSame, TwoThirdOneThird, StickyBanner, WallMixed, TitleTextCta, Mouseover, AnimatedSlide, Spacer, EditorialSlider} from '_uiComponents'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {LinkObject, Transformation, ShopImageData} from '_types'
import {PAGE_DESIGNER_MODULES, stableKeyFromComponent} from '_utils'
import {useGlobalConfigState} from '_context'

type UsePageDesignerProps = {
    pageId?: string
}

interface ComponentProps {
    component
    index: number
    isMainBanner?: boolean
    listingLayout?: string
    analyticsList?: string
    analyticsName?: string
    isBannerHalf?: boolean
    adaptiveHeight?: boolean
}

interface RenderComponentProps {
    component
    analyticsList?: string
    isMainBanner?: boolean
    listingLayout?: string
    analyticsName?: string
}

interface RenderRegionProps {
    region
    analyticsList?: string
    isMainBanner?: boolean
    listingLayout?: string
    analyticsName?: string
}

const SHOP_THE_IMAGE_TYPE_ID = `commerce_assets.${PAGE_DESIGNER_MODULES.SHOP_THE_IMAGE}`
const SHOP_THE_IMAGE_LINK_PREFIX = 'shop-the-image'
const SHOP_THE_IMAGE_LINK_PATTERN = /^shop-the-image(?:-?[A-Za-z0-9_-]+)?$/i

// Parses CTA links in the "shop-the-image(-<moduleId>)" format to extract the module id.
// This keeps the CTA format logic centralized across modules.
const parseShopTheImageLink = (ctaLink?: string) => {
    if (!ctaLink) return {isShopTheImageLink: false, moduleId: null}
    const trimmed = ctaLink.trim()
    if (!trimmed) return {isShopTheImageLink: false, moduleId: null}
    const sanitized = trimmed.replace(/^\/+/, '').split(/[?#]/)[0].replace(/\/$/, '')
    if (!sanitized) return {isShopTheImageLink: false, moduleId: null}
    if (!sanitized.toLowerCase().startsWith(SHOP_THE_IMAGE_LINK_PREFIX)) {
        return {isShopTheImageLink: false, moduleId: null}
    }
    const isMatch = SHOP_THE_IMAGE_LINK_PATTERN.test(sanitized)
    return {isShopTheImageLink: isMatch, moduleId: isMatch ? sanitized : null}
}

// Returns the moduleId only when the CTA link matches the expected pattern.
// No fallback is applied: without an explicit id, the drawer won't open.
const getShopTheImageModuleId = (ctaLink?: string) => {
    const {isShopTheImageLink, moduleId} = parseShopTheImageLink(ctaLink)
    return isShopTheImageLink && moduleId ? moduleId : null
}

// Builds the ShopTheImage data from the Page Designer payload.
// Page Designer enforces required fields, so we can trust moduleId/products here.
const buildShopImageData = (data): ShopImageData => {
    return {
        moduleTitle: data.moduleTitle ?? '',
        moduleId: data.moduleId,
        products: data.products,
    }
}

const getComponentKey = (component: any, index: number): string => {
    if (!component) return `pd-${index}`
    return component.id || stableKeyFromComponent(component) || `pd-${index}`
}

const getComponentId = (componentTypeId: string): string => {
    return componentTypeId?.replace('commerce_assets.', '').replace('commerce_layouts.', '')
}

// Returns the number of analytics-trackable items a Page Designer module represents.
// WallMixed counts its inner image/video items; AnimatedSlide sums its sub-module counts;
// ShopTheImage placeholders and typeless items count as 0; everything else counts as 1.
const getModuleItemCount = (item: any): number => {
    if (!item?.typeId || item.typeId === SHOP_THE_IMAGE_TYPE_ID) return 0
    const componentId = getComponentId(item.typeId)
    // if (componentId === PAGE_DESIGNER_MODULES.WALL_MIXED) {
    //     return item?.regions?.[0]?.components?.filter((inner) => inner?.typeId && inner.typeId !== SHOP_THE_IMAGE_TYPE_ID).length || 0
    // }
    if (componentId === PAGE_DESIGNER_MODULES.ANIMATED_SLIDE) {
        const slideItems = item?.regions?.find((r) => r.id === 'moduleItem')?.components ?? []
        return slideItems.reduce((sum, inner) => sum + getModuleItemCount(inner), 0)
    }
    return 1
}

/**
 * @name usePageDesigner
 *
 * @description
 * Custom hook for managing page designer components in a Salesforce Commerce Cloud environment.
 *
 * @hook
 * @param {Object} props - The properties object.
 * @param {string} [props.pageId] - The ID of the page to fetch components for.
 *
 * @returns {Object} An object containing the page components, error, and a function to render page designer components.
 * @returns {Array} props.pageComponents - An array of page components fetched from the API.
 * @returns {LinkObject} props.linkObject - A utility to handle URLs related to page designer.
 * @returns {boolean} props.isLoading - Indicates if the `usePage` query is currently loading.
 * @returns {Error} props.error - Any error that occurred during the API call.
 * @returns {function} props.renderPageDesignerComponent - Renders a page designer component with analytics metadata.
 *   @param {Object} component - The Page Designer component to render.
 *   @param {string} [analyticsList] - Analytics list context (e.g. category for PLP or editorial list for future extension to editorial pages).
 *   @param {boolean} [isMainBanner] - Whether the component is the main banner.
 *   @param {string} [listingLayout] - Layout identifier used by analytics/product impressions.
 *   @param {string} [analyticsName] - Analytics name to include in promo events.
 */
const usePageDesigner = ({pageId}: UsePageDesignerProps = {}) => {
    const positionRef = useRef(0)
    const {locale, site} = useMultiSite()
    const {appPreferences} = useGlobalConfigState()
    const {
        hostLink,
        CloudinaryImageTransformationsDPR,
        CloudinaryImageTransformationsFormat,
        CloudinaryImageTransformationsQuality,
        CloudinaryImageTransformations,
        CloudinaryVideoTransformationsQuality,
        CloudinaryVideoTransformationsBitRate,
        CloudinaryVideoTransformations,
        CloudinaryVideoFormat,
        CloudinaryVideoPosterTransformations,
        CloudinaryPageDesignerCNAME,
    } = appPreferences

    /**
     *  Ensure that `data?.hostLink` exists to avoid runtime errors.
     *  Note: This parsing is necessary to prevent blocking server-side rendering (SSR)
     *  due to potential latency in the response.
     */
    const parsedData = hostLink && JSON.parse(hostLink)

    /**
     * utility object for page designer image trasformation
     */
    const globalImageTransformation: Transformation = {
        raw_transformation: CloudinaryImageTransformations,
        dpr: CloudinaryImageTransformationsDPR && CloudinaryImageTransformationsDPR[0],
        fetchFormat: CloudinaryImageTransformationsFormat && CloudinaryImageTransformationsFormat[0],
        quality: CloudinaryImageTransformationsQuality && CloudinaryImageTransformationsQuality[0],
    }

    /**
     * utility object for page designer video trasformation
     */
    const globalVideoTransformation: Transformation = {
        raw_transformation: CloudinaryVideoTransformations,
        bit_rate: CloudinaryVideoTransformationsBitRate && CloudinaryVideoTransformationsBitRate[0],
        fetchFormat: CloudinaryVideoFormat && CloudinaryVideoFormat[0],
        quality: CloudinaryVideoTransformationsQuality && CloudinaryVideoTransformationsQuality[0],
        poster_image: CloudinaryVideoPosterTransformations,
    }

    /**
     * This is a utility object to handle URLs.
     * You can use it with `createRedirectUrl` to handle page designer hrefs.
     */
    const linkObject: LinkObject = {
        hostlink: parsedData?.[site.id],
        locale: locale.alias,
        site: site.id,
    }

    const {
        data: pageComponents,
        isLoading,
        error,
    } = usePage(
        {
            parameters: {
                pageId: pageId ?? null,
                aspectAttributes: `{"category": "${pageId!}"}`,
            },
        },
        {
            enabled: !!pageId,
        },
    )

    const getComponentKey = (component: any, index: number): string => {
        if (!component) return `pd-${index}`
        return component.id || stableKeyFromComponent(component) || `pd-${index}`
    }

    const getRenderableCount = (item) => getModuleItemCount(item)

    // Counts only renderable modules for analytics position (excludes 31_shopTheImage placeholders).
    // WallMixed counts its inner items; AnimatedSlide counts all its sub-module items recursively.
    const getComponentCount = (component) => {
        if (component?.components) {
            return component.components.reduce((total, inner) => total + getRenderableCount(inner), 0)
        }
        return getRenderableCount(component)
    }

    // Public renderer that also advances the shared position counter.
    const renderPageDesignerComponent = ({component, analyticsList = '', isMainBanner = false, listingLayout, analyticsName}: RenderComponentProps) => {
        if (!component?.typeId) return null

        const componentId = getComponentId(component?.typeId)
        const renderComponent = pageDesignerComponents[componentId]

        const startIndex = positionRef.current
        const count = getComponentCount(component)
        positionRef.current += count

        return renderComponent
            ? renderComponent({
                  component,
                  index: startIndex,
                  analyticsList,
                  isMainBanner,
                  listingLayout,
                  analyticsName,
              })
            : null
    }

    const renderPageDesignerRegion = ({region, analyticsList = '', isMainBanner = false, listingLayout, analyticsName}: RenderRegionProps) => {
        const isBannerHalf = region?.id === 'banner_half'

        let cumulativePosition = positionRef.current
        positionRef.current += getComponentCount(region)

        return region?.components?.map((component) => {
            if (!component?.typeId) return null

            const componentId = getComponentId(component?.typeId)
            const renderComponent = pageDesignerComponents[componentId]

            const componentIndex = cumulativePosition
            const count = getRenderableCount(component)
            cumulativePosition += count

            return renderComponent
                ? renderComponent({
                      component,
                      index: componentIndex,
                      analyticsList: analyticsList,
                      isMainBanner,
                      listingLayout,
                      analyticsName,
                      isBannerHalf,
                  })
                : null
        })
    }

    return {
        pageComponents,
        linkObject,
        error,
        isLoading,
        renderPageDesignerComponent,
        renderPageDesignerRegion,
        resetPageDesignerPosition: () => {
            positionRef.current = 0
        },
        globalImageTransformation,
        globalVideoTransformation,
        CloudinaryPageDesignerCNAME,
    }
}

// Example components for page designer
const pageDesignerComponents = {
    [PAGE_DESIGNER_MODULES.HERO_XL_FULL]: ({
        component,
        index,
        isMainBanner,
        listingLayout,
        analyticsList,
        analyticsName,
        isBannerHalf,
        adaptiveHeight,
    }: ComponentProps) => {
        const {data, name} = component ?? {}
        const key = getComponentKey(component, index)
        return (
            <HeroBanner
                key={key}
                imgDesk={data?.cld_image_desktop}
                imgMobile={data?.cld_image_mobile}
                imgTablet={data?.cld_image_tablet}
                imgDeskRatio={data?.image_ratio_desktop}
                imgTabletRatio={data?.image_ratio_tablet}
                imgMobileRatio={data?.image_ratio_mobile}
                padding={data?.image_with_padding === 'yes' ? true : false}
                imgFullHeight={data?.image_full_height === 'yes' ? true : false}
                ctaText={data?.cta_text}
                ctaLink={data?.cta_url}
                ctaAccesibility={data?.accesibility_cta_text}
                ctaText2={data?.cta2_text}
                ctaLink2={data?.cta2_url}
                ctaAccesibility2={data?.accesibility_cta_2_text}
                verticalAlign={data?.content_v_align}
                horizontalAlign={data?.content_h_align}
                classes={data?.class_name}
                extraCSS={data?.custom_css}
                eyelet={data?.eyelet_text}
                title={data?.title_text}
                text={data?.description_text}
                ctaInlineDesk={data?.cta_inline_d}
                ctaInlineMobile={data?.cta_inline_m}
                ctaStyle={data?.cta_style}
                colorContent={data?.content_color}
                colorCta={data?.cta_color}
                bgCta={data?.cta_background}
                contentTextAlign={data?.content_t_align}
                isShowScroll={data?.scrollDown}
                countdownTimer={data?.countdown}
                isImgZoom={data?.animation_enabled}
                animationTime={data?.animation_duration}
                scrollArrowColor={data?.scrollDownColor}
                isAnimationTitle={data?.animation_text}
                isAnimationCta={data?.animation_cta_text}
                imgTitle={data?.image_title}
                analyticsID={name}
                position={Number(index) + 1}
                isMainBanner={isMainBanner}
                listingLayout={listingLayout!}
                analyticsList={analyticsList}
                textBottomMobile={data?.mobile_text_below_image}
                analyticsName={analyticsName}
                isBannerHalf={isBannerHalf}
                adaptiveHeight={adaptiveHeight}
                editorialSearch={data?.editorial_search}
                editorialSearchHint={data?.editorial_search_hint}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.HERO_XL_VIDEO_CLOUDINARY]: ({
        component,
        index,
        isMainBanner,
        listingLayout,
        analyticsList,
        analyticsName,
        isBannerHalf,
        adaptiveHeight,
    }: ComponentProps) => {
        const {data, name} = component ?? {}
        const key = getComponentKey(component, index)
        return (
            <HeroBanner
                key={key}
                videoDesk={data?.iframe_id}
                videoMobile={data?.iframem_id}
                padding={data?.video_with_padding === 'yes' ? true : false}
                ctaText={data?.cta_text}
                ctaLink={data?.cta_url}
                ctaAccesibility={data?.accesibility_cta_text}
                ctaText2={data?.cta2_text}
                ctaLink2={data?.cta2_url}
                ctaAccesibility2={data?.accesibility_cta_2_text}
                verticalAlign={data?.content_v_align}
                horizontalAlign={data?.content_h_align}
                classes={data?.class_name}
                extraCSS={data?.custom_css}
                eyelet={data?.eyelet_text}
                title={data?.title_text}
                text={data?.description_text}
                ctaInlineDesk={data?.cta_inline_d}
                ctaInlineMobile={data?.cta_inline_m}
                ctaStyle={data?.cta_style}
                colorContent={data?.content_color}
                colorCta={data?.cta_color}
                bgCta={data?.cta_background}
                contentTextAlign={data?.content_t_align}
                isShowScroll={data?.scrollDown}
                countdownTimer={data?.countdown}
                isImgZoom={data?.animation_enabled}
                animationTime={data?.animation_duration}
                scrollArrowColor={data?.scrollDownColor}
                isAnimationTitle={data?.animation_text}
                isAnimationCta={data?.animation_cta_text}
                analyticsID={name}
                position={Number(index) + 1}
                isMainBanner={isMainBanner}
                listingLayout={listingLayout!}
                analyticsList={analyticsList}
                textBottomMobile={data?.mobile_text_below_image}
                analyticsName={analyticsName}
                isBannerHalf={isBannerHalf}
                adaptiveHeight={adaptiveHeight}
                editorialSearch={data?.editorial_search}
                editorialSearchHint={data?.editorial_search_hint}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.DOUBLE_SAME_CLOUDINARY]: ({component, index, listingLayout, analyticsList, analyticsName, adaptiveHeight}: ComponentProps) => {
        const {data, name, regions} = component ?? {}
        const key = getComponentKey(component, index)
        return (
            <DoubleSame
                key={key}
                isMobileRow={data?.mobile_side_by_side}
                classes={data?.class_name}
                extraCSS={data?.custom_css}
                backgroundColor={data?.module_background}
                isBeforeRightMobile={data?.mobile_right_before}
                analyticsList={analyticsList}
                adaptiveHeight={adaptiveHeight}
                leftElement={{
                    type: data?.element1_type,
                    imgDesk: data?.cld_image_desktop_1,
                    imgMobile: data?.cld_image_mobile_1,
                    imgTablet: data?.cld_image_tablet_1,
                    imgTitle: data?.image1_title,
                    imgDeskRatio: data?.image_ratio_desktop,
                    imgTabletRatio: data?.image_ratio_tablet,
                    imgMobileRatio: data?.image_ratio_mobile,
                    video: data?.iframe1_id,
                    padding: data?.image1_with_padding === 'yes' ? true : false,
                    layout: data?.element1_layout === 'Padded' ? 'padded' : data?.element1_layout === 'Close Padded' ? 'close_padded' : 'full',
                    ctaText: data?.cta1_text,
                    ctaLink: data?.cta1_url,
                    ctaBlank: data?.cta1_target_blank,
                    ctaStyle: data?.cta1_style,
                    verticalAlign: data?.content1_v_align,
                    horizontalAlign: data?.content1_h_align,
                    textAlign: data?.content1_t_align,
                    color: data?.cta1_color,
                    title: data?.title_text1,
                    text: data?.description_text1,
                    isImgZoom: data?.animation_enabled,
                    isHoverZoomLeft: data?.image_hover_zoom_left === 'Yes' ? true : false,
                    animationTime: data?.animation_duration,
                    isAnimationCta: data?.animation_cta_text,
                    isAnimationTitle: data?.animation_text,
                    listingLayout: listingLayout,
                }}
                rightElement={{
                    type: data?.element2_type,
                    imgDesk: data?.cld_image_desktop_2,
                    imgMobile: data?.cld_image_mobile_2,
                    imgTablet: data?.cld_image_tablet_2,
                    imgDeskRatio: data?.image_ratio_desktop,
                    imgTabletRatio: data?.image_ratio_tablet,
                    imgMobileRatio: data?.image_ratio_mobile,
                    imgTitle: data?.image2_title,
                    video: data?.iframe2_id,
                    padding: data?.image2_with_padding === 'yes' ? true : false,
                    layout: data?.element2_layout === 'Padded' ? 'padded' : data?.element2_layout === 'Close Padded' ? 'close_padded' : 'full',
                    ctaText: data?.cta2_text,
                    ctaLink: data?.cta2_url,
                    ctaBlank: data?.cta2_target_blank,
                    ctaStyle: data?.cta2_style,
                    verticalAlign: data?.content2_v_align,
                    horizontalAlign: data?.content2_h_align,
                    textAlign: data?.content2_t_align,
                    color: data?.cta2_color,
                    title: data?.title_text2,
                    text: data?.description_text2,
                    isImgZoom: data?.animation_enabled,
                    isHoverZoomRight: data?.image_hover_zoom_right === 'Yes' ? true : false,
                    animationTime: data?.animation_duration,
                    isAnimationCta: data?.animation_cta_text,
                    isAnimationTitle: data?.animation_text,
                    listingLayout: listingLayout,
                }}
                isLeftCarousel={data?.element1_carousel === 'Yes' ? true : false}
                isRightCarousel={data?.element2_carousel === 'Yes' ? true : false}
                analyticsID={name}
                position={Number(index) + 1}
                leftItem={regions[0]?.components?.map((comp) => comp.data?.product)}
                rightItem={regions[1]?.components?.map((comp) => comp.data?.product)}
                analyticsName={analyticsName}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.TITLE_TEXT_CTA]: ({component, index, analyticsList, analyticsName}: ComponentProps) => {
        const {data, name} = component ?? {}
        const key = getComponentKey(component, index)
        return (
            <TitleTextCta
                key={key}
                position={Number(index) + 1}
                analyticsID={name}
                eyelet={data?.eyelet_text}
                title={data?.title_text}
                description={data?.description_text}
                ctaText={data?.cta_text}
                ctaUrl={data?.cta_url}
                ctaTargetBlank={data?.cta1_target_blank}
                cta2Text={data?.cta2_text}
                cta2Url={data?.cta2_url}
                cta2TargetBlank={data?.cta2_target_blank}
                ctaStyle={data?.cta_style}
                fullHeight={data?.full_height === 'yes'}
                textAlign={data?.content_t_align}
                horizontalPosition={data?.content_h_align}
                contentColor={data?.content_color}
                contentBackground={data?.content_background}
                scrollDownArrow={data?.scrollDown}
                ctaTextAnimation={data?.animation_cta_text}
                textAnimation={data?.animation_text}
                classes={data?.class_name}
                extraCSS={data?.custom_css}
                analyticsList={analyticsList}
                analyticsName={analyticsName}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.MOUSEOVER]: ({component, index, listingLayout, analyticsList, analyticsName, adaptiveHeight}: ComponentProps) => {
        const {data, name} = component ?? {}
        const key = getComponentKey(component, index)
        const sections = ([1, 2, 3, 4, 5, 6] as const).map((n) => ({
            text: data?.[`text${n}`],
            accessibilityText: data?.[`accessibility_text_section_${n}`],
            link: data?.[`link${n}`],
            cldImageDesktop: data?.[`cld_image_desktop_${n}`],
            imageDesktop: data?.[`image_desktop${n}`],
        }))
        return (
            <Mouseover
                key={key}
                imgDeskDefault={data?.cld_image_desktop_0 || data?.image_desktop0}
                imgTabletDefault={data?.cld_image_tablet_0 || data?.image_tablet0}
                imgMobileDefault={data?.cld_image_mobile_0 || data?.image_mobile0}
                sections={sections}
                title={data?.title}
                colorCta={data?.cta_color}
                fullHeightSection={data?.full_height_section === 'yes'}
                classes={data?.class_name}
                extraCSS={data?.custom_css}
                analyticsID={name}
                analyticsList={analyticsList}
                listingLayout={listingLayout}
                analyticsName={analyticsName}
                position={Number(index) + 1}
                adaptiveHeight={adaptiveHeight}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.TWO_THIRDS_ONE_THIRD_CLOUDINARY]: ({component, index, listingLayout, analyticsList, analyticsName}: ComponentProps) => {
        const {data, name} = component ?? {}
        const key = getComponentKey(component, index)
        return (
            <TwoThirdOneThird
                key={key}
                classes={data?.class_name}
                extraCSS={data?.custom_css}
                backgroundColor={data?.module_background}
                isBeforeRightMobile={data?.content2_mobile_first}
                isBeforeRightDesk={data?.content2_desktop_first}
                analyticsID={name}
                position={Number(index) + 1}
                analyticsList={analyticsList}
                leftElement={{
                    type: data?.element1_type,
                    imgDesk: data?.cld_image_desktop_1,
                    imgMobile: data?.cld_image_mobile_1,
                    imgTablet: data?.cld_image_tablet_1,
                    imgTitle: data?.image1_title,
                    video: data?.iframe1_id,
                    padding: data?.image1_with_padding === 'yes' ? true : false,
                    ctaText: data?.cta1_text,
                    ctaLink: data?.cta1_url,
                    ctaStyle: data?.cta1_style,
                    verticalAlign: data?.content1_v_align,
                    horizontalAlign: data?.content1_h_align,
                    textAlign: data?.content1_t_align,
                    color: data?.cta1_color,
                    title: data?.title_text1,
                    text: data?.description_text1,
                    listingLayout: listingLayout,
                }}
                rightElement={{
                    type: data?.element2_type,
                    imgDesk: data?.cld_image_desktop_2,
                    imgMobile: data?.cld_image_mobile_2,
                    imgTablet: data?.cld_image_tablet_2,
                    imgTitle: data?.image2_title,
                    video: data?.iframe2_id,
                    padding: data?.image2_with_padding === 'yes' ? true : false,
                    ctaText: data?.cta2_text,
                    ctaLink: data?.cta2_url,
                    ctaStyle: data?.cta2_style,
                    verticalAlign: data?.content2_v_align,
                    horizontalAlign: data?.content2_h_align,
                    textAlign: data?.content2_t_align,
                    color: data?.cta2_color,
                    title: data?.title_text2,
                    text: data?.description_text2,
                    listingLayout: listingLayout,
                }}
                analyticsName={analyticsName}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.STICKY_BANNER]: ({component, index, analyticsName}: ComponentProps) => {
        const {data, name} = component ?? {}
        const key = getComponentKey(component, index)
        return (
            <StickyBanner
                key={key}
                classes={data?.class_name}
                extraCSS={data?.custom_css}
                imgDesk={data?.cld_image_desktop}
                imgMobile={data?.cld_image_mobile}
                imgTablet={data?.cld_image_tablet}
                title={data?.title}
                subtitle={data?.subtitle}
                text={data?.text}
                countdownTimer={data?.countdown}
                ctaText={data?.cta_text}
                ctaLink={data?.cta_url}
                colorContent={data?.content_color}
                backgroundColor={data?.content_background}
                cookieID={data?.id_cookie}
                cookieTime={data?.time_cookie}
                analyticsID={name}
                position={Number(index) + 1}
                analyticsName={analyticsName}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.STICKY_BANNER_VIDEO]: ({component, index, analyticsName}: ComponentProps) => {
        const {data, name} = component ?? {}
        const key = getComponentKey(component, index)
        return (
            <StickyBanner
                key={key}
                classes={data?.class_name}
                extraCSS={data?.custom_css}
                videoDesk={data?.iframe_id}
                videoMobile={data?.iframem_id}
                title={data?.title}
                subtitle={data?.subtitle}
                text={data?.text}
                countdownTimer={data?.countdown}
                ctaText={data?.cta_text}
                ctaLink={data?.cta_url}
                colorContent={data?.content_color}
                backgroundColor={data?.content_background}
                cookieID={data?.id_cookie}
                cookieTime={data?.time_cookie}
                analyticsID={name}
                position={Number(index) + 1}
                analyticsName={analyticsName}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.WALL_MIXED]: ({component, index, listingLayout, analyticsList, analyticsName, adaptiveHeight}: ComponentProps) => {
        const {data, name, regions} = component ?? {}
        const key = getComponentKey(component, index)
        const positionStart = Number(index) + 1
        return (
            <WallMixed
                key={key}
                data={data}
                regions={regions}
                positionStart={positionStart}
                analyticsID={name}
                analyticsList={analyticsList}
                listingLayout={listingLayout}
                analyticsName={analyticsName}
                adaptiveHeight={adaptiveHeight}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.SHOP_THE_IMAGE]: ({component, analyticsList}: ComponentProps) => {
        const shopImageData = buildShopImageData(component?.data as ShopImageData | undefined)
        if (!shopImageData) return null
        return (
            <Box
                key={shopImageData.moduleId}
                // Hidden placeholder used by openShopTheImageDrawer to fetch module data from the DOM.
                data-testid="shop-the-image-placeholder"
                display="none"
                data-module-id={shopImageData.moduleId}
                data-module-title={shopImageData.moduleTitle}
                data-products={shopImageData.products}
                data-analytics-list={analyticsList}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.EDITORIAL_SLIDER]: ({component, index, listingLayout, analyticsList, analyticsName}: ComponentProps) => {
        const {data, regions} = component ?? {}
        const key = getComponentKey(component, index)
        const positionStart = Number(index) + 1
        return (
            <EditorialSlider
                key={key}
                data={data}
                regions={regions}
                positionStart={positionStart}
                listingLayout={listingLayout}
                analyticsList={analyticsList}
                analyticsName={analyticsName}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.ANIMATED_SLIDE]: ({component, index, listingLayout, analyticsList, analyticsName}: ComponentProps) => {
        const {data, name, regions} = component ?? {}
        const key = getComponentKey(component, index)
        const moduleItems = regions?.find((region) => region.id === 'moduleItem')?.components
        let cumulativeIndex = index
        return (
            <AnimatedSlide
                key={key}
                data={data}
                moduleItems={moduleItems?.map((moduleItem) => {
                    const componentId = getComponentId(moduleItem.typeId)
                    const renderComponent = pageDesignerComponents[componentId]
                    const currentIndex = cumulativeIndex
                    cumulativeIndex += getModuleItemCount(moduleItem)

                    return renderComponent
                        ? renderComponent({
                              component: moduleItem,
                              index: currentIndex,
                              analyticsList: analyticsList,
                              listingLayout,
                              analyticsName,
                              adaptiveHeight: true,
                          })
                        : null
                })}
            />
        )
    },
    [PAGE_DESIGNER_MODULES.SPACER]: ({component}: ComponentProps) => {
        const {data} = component ?? {}

        return (
            <Spacer
                spaceDesktop={data?.space_desktop}
                spaceTablet={data?.space_tablet}
                spaceMobile={data?.space_mobile}
                backgroundColor={data?.background_color}
                additionalClass={data?.class_name}
                extraCSS={data?.custom_css}
            ></Spacer>
        )
    },
}

export {usePageDesigner, getShopTheImageModuleId}
